import { useCallback, useRef } from "react"
import { Loader2, Mic, MicOff, Square } from "lucide-react"

import { cn } from "@/lib/utils"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useAudioRecorder } from "@/hooks/use-audio-recorder"

/**
 * Textarea with a mic button that records audio, uploads it to the server for
 * transcription (via OpenRouter's openai/gpt-audio-mini model), and appends
 * the transcript to the existing field value.
 *
 * Drop-in replacement for a Label + Textarea pair:
 *   <VoiceInputTextarea
 *     id="why_crucial"
 *     label="Why is this Crucial?"
 *     required
 *     placeholder="Why does this matter now?"
 *     rows={3}
 *     value={form.data.why_crucial}
 *     onChange={(value) => form.setData("why_crucial", value)}
 *     error={form.errors.why_crucial}
 *   />
 *
 * If the browser does not support MediaRecorder/getUserMedia, the mic button
 * is silently not rendered — the component falls back to a plain Textarea.
 */
export function VoiceInputTextarea({
  id,
  label,
  required = false,
  placeholder,
  rows = 3,
  value,
  onChange,
  error,
  className,
  textareaClassName,
  // Optional helper copy shown under the field. Forms that want a single
  // page-level privacy note should leave this null and render it themselves.
  helpText = null,
}) {
  // Keep a ref to the current value so the transcript handler always sees the
  // freshest text even if multiple re-renders happen during upload.
  const valueRef = useRef(value)
  valueRef.current = value

  const handleTranscript = useCallback(
    (transcript) => {
      const current = (valueRef.current || "").trimEnd()
      const next = current ? `${current} ${transcript}` : transcript
      onChange?.(next)
    },
    [onChange]
  )

  const { isSupported, state, error: recorderError, toggleRecording } =
    useAudioRecorder({ onTranscript: handleTranscript })

  const isRecording = state === "recording"
  const isBusy = state === "requesting" || state === "uploading"
  const isError = state === "error"

  // A blocked microphone used to make this button a permanent no-op, on the
  // reasoning that re-asking would only re-trigger NotAllowedError. It also
  // meant the only way back was a page reload — mid-form, that costs the user
  // everything they had typed. `toggleRecording` retries instead, which is
  // exactly what recovers the button once they allow it in the address bar,
  // and the error text stays visible either way.
  const handleMicClick = toggleRecording

  const micIcon = isBusy ? (
    <Loader2 className="h-4 w-4 animate-spin" />
  ) : isRecording ? (
    <Square className="h-3.5 w-3.5 fill-current" />
  ) : isError ? (
    <MicOff className="h-4 w-4" />
  ) : (
    <Mic className="h-4 w-4" />
  )

  const micLabel = isRecording
    ? "Stop recording"
    : isBusy
    ? state === "requesting"
      ? "Requesting microphone access"
      : "Transcribing"
    : isError
    ? "Try the microphone again"
    : "Start voice input"

  return (
    <div className={cn("space-y-2", className)}>
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
      )}
      <div className="relative">
        <Textarea
          id={id}
          placeholder={placeholder}
          rows={rows}
          className={cn(
            "bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400",
            // Reserve room for the mic button so text doesn't flow under it.
            isSupported && "pr-12",
            textareaClassName
          )}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          disabled={isBusy}
        />
        {isSupported && (
          <button
            type="button"
            onClick={handleMicClick}
            disabled={isBusy}
            aria-label={micLabel}
            aria-pressed={isRecording}
            title={micLabel}
            className={cn(
              "absolute bottom-2 right-2 inline-flex h-9 w-9 items-center justify-center rounded-md",
              "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-400",
              !isRecording &&
                !isError &&
                "text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100",
              isRecording &&
                "bg-red-500 text-white hover:bg-red-600 animate-pulse ring-2 ring-red-300",
              isError && "text-red-600 hover:bg-red-50",
              isBusy && "text-zinc-400 cursor-not-allowed"
            )}
          >
            {micIcon}
          </button>
        )}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {recorderError && !error && (
        <p className="text-sm text-red-600">{recorderError}</p>
      )}
      {isSupported && helpText && !error && !recorderError && (
        <p className="text-xs text-zinc-500">{helpText}</p>
      )}
    </div>
  )
}
